import "./style.css";
import { useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { gql, useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocation, useNavigate } from "react-router-dom";

import { listUserHorses } from "apollo/queries/horses";
import { LIST_SADDLE_FAVORITES } from "apollo/queries/saddleFavorite";
import { GET_SADDLE_DETAIL_QUERY } from "apollo/queries/getSaddleDetails";
import { useBrandDisciplineStore } from "stores/brandDiscipline/useBrandDisciplineStore";
import { useLoggedIn } from "stores/auth/authStore";
import { LoadingSpinner } from "./components/Spinner";
import { DesktopView } from "./sections/DesktopView";
import { MobileView } from "./sections/MobileView";
import ShoppingCartModal from "components/Modal/ShoppingCart";

import { saddleSchema, useSaddleMutations } from "./utils";

export const SaddleDetails = () => {
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const { user, setIsAction } = useLoggedIn();
  const actionState = useBrandDisciplineStore((state) => state.actionState);

  const methods = useForm({
    resolver: zodResolver(saddleSchema),
    defaultValues: {
      size: "",
      color: "",
      selectedHorse: null,
      selectedHorseStatus: null,
      updatedSaddle: null,
      favouriteLoading: false,
      currentIndex: 0,
    },
  });
  const { control, watch, setValue } = methods;

  const { data: horsesData } = useQuery(gql(listUserHorses), { skip: !user });
  const { data: favoritesData } = useQuery(gql(LIST_SADDLE_FAVORITES), {
    skip: !user,
    fetchPolicy: "network-only",
  });
  const { loading, data } = useQuery(gql(GET_SADDLE_DETAIL_QUERY), {
    variables: {
      id: locationState?.item?.sk,
      horseId: locationState?.selectedHorse?.value,
      brandName: locationState?.item?.brandName,
    },
  });

  // AppSync doesn't guarantee item order; the PRODUCT row may not be index 0.
  const saddle = data?.listSaddleDetail?.find((item) => item?.type === "PRODUCT");
  const { handleFavourite } = useSaddleMutations(
    setValue,
    watch("selectedHorse")
  );

  // Dedup sizes: a saddle with N colors × M sizes returns N×M VARIANT rows,
  // which would otherwise render the same size M times in the dropdown.
  const seatSizes = useMemo(() => {
    const seen = new Set();
    const ordered = [];
    for (const item of data?.listSaddleDetail || []) {
      if (item?.type !== "VARIANT") continue;
      const n = parseFloat(item?.size);
      if (!Number.isFinite(n) || seen.has(n)) continue;
      seen.add(n);
      ordered.push({ label: n.toString(), value: n });
    }
    ordered.sort((a, b) => a.value - b.value);
    return ordered;
  }, [data]);

  const colors = useMemo(() => {
    const variantColors =
      data?.listSaddleDetail
        ?.filter((item) => item?.type === "VARIANT" && item?.color)
        ?.map((item) => item.color) || [];
    const productHint =
      data?.listSaddleDetail?.find((item) => item?.type === "PRODUCT")
        ?.availableColors || [];
    const seen = new Set();
    const ordered = [];
    for (const c of [...productHint, ...variantColors]) {
      const key = String(c ?? "").trim().toLowerCase();
      if (key && !seen.has(key)) {
        seen.add(key);
        ordered.push({ label: c, value: c });
      }
    }
    return ordered;
  }, [data]);

  const favouriteList =
    favoritesData?.listSaddleFavorites?.map((item) => item.saddleId) || [];
  const filteredSaddle = watch("updatedSaddle") || saddle;

  useEffect(() => {
    const horseOptions = horsesData?.listUserHorses?.items.map((item) => ({
      value: item?.id,
      label: item?.name,
    }));
    if (horseOptions?.length > 0) {
      const selectedHorseData = horsesData?.listUserHorses?.items?.find(
        (item) => item?.id === locationState?.selectedHorse?.value
      );
      setValue("selectedHorseStatus", selectedHorseData?.status);
      setValue("selectedHorse", horseOptions[0]);
    }
  }, [horsesData, setValue, locationState?.selectedHorse?.value]);

  // Subscribe to size/color via useWatch so this effect only fires when those
  // specific fields change (instead of every parent re-render via watch()).
  const selectedSize = useWatch({ control, name: "size" });
  const selectedColor = useWatch({ control, name: "color" });

  // Pre-fill size/color from the variant the user clicked in the list page, but
  // only if they haven't already interacted with the dropdowns. Match against the
  // resolved dropdown options so we don't set a value the <Select> can't render.
  useEffect(() => {
    if (!data || !locationState?.item) return;
    const incoming = locationState.item;
    if ((selectedSize == null || selectedSize === "") && incoming?.size != null) {
      const n = parseFloat(incoming.size);
      if (Number.isFinite(n) && seatSizes.some((o) => o.value === n)) {
        setValue("size", n, { shouldDirty: false });
      }
    }
    if (!selectedColor && incoming?.color) {
      const norm = (c) => String(c ?? "").trim().toLowerCase();
      const target = norm(incoming.color);
      const match = colors.find((o) => norm(o.value) === target);
      if (match) setValue("color", match.value, { shouldDirty: false });
    }
    // Intentionally not depending on selectedSize/selectedColor so a manual
    // clear by the user doesn't snap back to the URL state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, locationState?.item, seatSizes, colors, setValue]);

  useEffect(() => {
    if (selectedSize == null || selectedSize === "") {
      setValue("updatedSaddle", null, { shouldDirty: true });
      return;
    }
    const needsColor = colors.length > 0;
    if (needsColor && !selectedColor) {
      setValue("updatedSaddle", null, { shouldDirty: true });
      return;
    }
    const sizeNum = Number(selectedSize);
    const normColor = (c) => String(c ?? "").trim().toLowerCase();
    const targetColor = needsColor ? normColor(selectedColor) : null;
    const selectedVariant = data?.listSaddleDetail?.find(
      (item) =>
        item?.type === "VARIANT" &&
        parseFloat(item?.size) === sizeNum &&
        (!needsColor || normColor(item?.color) === targetColor)
    );
    setValue("updatedSaddle", selectedVariant ?? null, { shouldDirty: true });
  }, [selectedSize, selectedColor, data, setValue, colors.length]);

  useEffect(() => {
    if (window.klaviyo && saddle) {
      try {
        window.klaviyo.push(["track", "Viewed Product", { ...saddle }]);
      } catch (error) {
        console.error("Failed to track 'Viewed Product' event:", error);
      }
    }
  }, [saddle]);

  const handleImageClick = (index) => setValue("currentIndex", index);
  const handleCheckAuth = () => {
    setIsAction("saddleDetails");
    if (!user) methods.open("Login");
  };

  const commonProps = {
    navigate,
    filteredSaddle,
    favouriteList,
    handleFavourite,
    handleImageClick,
    handleCheckAuth,
    user,
    control,
    seatSizes,
    colors,
    saddle,
    locationState,
    watch,
    setValue,
    methods,
  };

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <DesktopView {...commonProps} />
      <MobileView {...commonProps} />
      {actionState?.adding !== undefined && (
        <ShoppingCartModal
          isAdding={actionState.adding}
          description={
            !actionState.adding &&
            "The Saddle is already in your shopping cart."
          }
        />
      )}
    </>
  );
};
